import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';

import { ONE } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { isMomentsSession, SessionListType, SessionStatus } from '@/types';
import {
  Avatar,
  Button,
  Chip,
  ClickAwayListener,
  Icon,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@/ui';
import { track } from '@/utils';

import Feedback from '../../Feedback';
import SessionReflectionChip from '../../ReflectionChip';
import {
  isMemberReflectionProfile,
  MEMBER_REFLECTION_PROFILE_SLUGS,
} from '../../memberReflectionProfiles';
import { useSessionNotTranscribed } from '../../useSessionNotTranscribed';
import MemberSessionActionButtons from '../MemberSessionActionButtons';

import type { Session } from '@/types';
import type { FC } from 'react';

import { useSessionJoinUrl } from '@/features/appointments/useSessionJoinUrl';
import { useGetMomentsSessionArtifactsQuery } from '@/store/api/momentsApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

interface MemberSessionCardProps {
  onCancel?: (session: Session) => void;
  onFeedback?: (session: Session) => void;
  onReschedule?: (session: Session) => void;
  session: Session;
  // Lay the row out per the Figma "Past Sessions" frame (17420:41868) rather
  // than for the continuous timeline: reserve the frame's 105px gap before the
  // actions column at md+, and drop the per-row "Attended" chip because the
  // surrounding list is already scoped to past sessions. Off by default so
  // `SessionTimeline`, which mixes upcoming and past rows under its own design,
  // is unaffected.
  pastListLayout?: boolean;
  // Native-only past Appointments do not have a Rails Session/User projection,
  // so legacy detail, feedback, profile, and chat actions would target the
  // wrong identifiers. Render those rows as informational until a projection
  // exists.
  pastReadOnly?: boolean;
  // Member's IANA timezone; single source of truth for time display (design §5).
  // Defaults to the browser tz when not provided.
  timeZone?: string;
  type: SessionListType;
}

// Leads with the session DATE (member view), unlike the coach view which leads
// with the session title.
const formatWeekdayDate = (value: string, timeZone: string): string =>
  new Date(value).toLocaleDateString('en', {
    day: 'numeric',
    month: 'long',
    timeZone,
    weekday: 'long',
  });

// "10:00-10:45 AM" — collapses a shared AM/PM to a single suffix and omits the
// GMT suffix (the timeline shows a single "Times shown in {tz}" hint instead),
// matching the Figma design.
const formatTimeRange = (
  start: string,
  end: string,
  timeZone: string,
): string => {
  const opts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  };
  const startStr = new Date(start).toLocaleTimeString('en', opts);
  const endStr = new Date(end).toLocaleTimeString('en', opts);
  const startMeridiem = startStr.slice(-2);
  const endMeridiem = endStr.slice(-2);
  // Drop the start meridiem when both sides share it (e.g. "10:00-10:45 AM").
  const startTime =
    startMeridiem === endMeridiem
      ? startStr.replace(/\s?[AP]M$/u, '')
      : startStr;
  return `${startTime}-${endStr}`;
};

const dateKeyInTimeZone = (value: Date | string, timeZone: string): string =>
  new Date(value).toLocaleDateString('en-CA', { timeZone });

const shortTimeZoneName = (value: string, timeZone: string): string =>
  new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'short' })
    .formatToParts(new Date(value))
    .find((part) => part.type === 'timeZoneName')?.value ?? timeZone;

const MemberSessionCard: FC<MemberSessionCardProps> = ({
  onCancel = undefined,
  onFeedback = undefined,
  onReschedule = undefined,
  pastListLayout = false,
  pastReadOnly = false,
  session,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  type,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    currentData: currentUserData,
    isError: isCurrentUserError,
    isUserLoading,
    user,
  } = useCurrentUser();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { coach } = session;
  const actionContext = `${session.title} with ${coach.fullName}`;
  const dateHeadline = formatWeekdayDate(session.startedAt, timeZone);
  const timeRange = formatTimeRange(
    session.startedAt,
    session.endedAt,
    timeZone,
  );
  const timeZoneName = shortTimeZoneName(session.startedAt, timeZone);

  const isDone = session.status === SessionStatus.DONE;
  const isCancelled = session.status === SessionStatus.CANCELLED;
  const isPast = type === SessionListType.Past;
  const shouldQueryReflection =
    isPast &&
    !pastReadOnly &&
    !isUserLoading &&
    !isCurrentUserError &&
    !!currentUserData?.id &&
    currentUserData.id === user.id &&
    user.id === session.userId &&
    !!session.id;
  const { data: momentsSessionArtifacts } = useGetMomentsSessionArtifactsQuery(
    shouldQueryReflection
      ? {
          includeContent: true,
          legacySessionId: session.id,
          profileSlug: MEMBER_REFLECTION_PROFILE_SLUGS,
        }
      : skipToken,
  );
  const reflectionArtifact = momentsSessionArtifacts?.items.find((artifact) =>
    isMemberReflectionProfile(artifact.profileSlug),
  );
  const showReflectionLink = shouldQueryReflection && !!reflectionArtifact;
  // A reflection implies a transcript, so the label only speaks for the cards
  // that have no chip to show.
  const notTranscribed = useSessionNotTranscribed({
    legacySessionId: session.id,
    skip: !shouldQueryReflection,
  });
  const subhead = `${session.title} · ${coach.fullName}${
    shouldQueryReflection && notTranscribed && !showReflectionLink
      ? ' · Not transcribed'
      : ''
  }`;
  const cardHeadline = pastListLayout
    ? dateHeadline
    : `${dateHeadline} · ${timeRange} ${timeZoneName}`;
  const cardSubhead = pastListLayout
    ? `${coach.fullName} · ${timeRange} ${timeZoneName}`
    : subhead;
  const showCancel =
    Boolean(onCancel) && session.sourceAppointmentCancelAllowed !== false;
  // Members join today's live session directly from the card (parity with the
  // legacy SessionCard: https location + happening today, not done/cancelled).
  const joinable =
    !isDone &&
    !isCancelled &&
    dateKeyInTimeZone(session.startedAt, timeZone) ===
      dateKeyInTimeZone(new Date(), timeZone);
  const joinUrl = useSessionJoinUrl(session, { enabled: joinable });
  const showJoinNow = !!joinUrl?.startsWith('https:') && joinable;

  const handleJoin = () => window.open(joinUrl);

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    track('Clicked Member Session Card Menu');
    setAnchorEl(e.currentTarget);
  };

  const handleOnClose = (e: Event | React.SyntheticEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleFeedback = (e: React.MouseEvent) => {
    handleOnClose(e);
    onFeedback?.(session);
  };

  const handleViewProfile = (e: React.MouseEvent) => {
    handleOnClose(e);
    navigate(`/m/profiles-coach/${coach.id}`);
  };

  const handleMessage = (e: React.MouseEvent) => {
    handleOnClose(e);
    dispatch(setChatId(coach.cometChatId));
  };

  return (
    <Paper
      className={clsx(
        'flex min-h-[90px] w-full items-start justify-between gap-4 rounded-xl bg-white p-6 max-md:items-center',
        // Figma "Past Sessions" (17420:41868) reserves 105px between the
        // content column and the actions column: content 765 + 105 + actions 42
        // = the 912px inner width of a 960px card. From md up only; the frame
        // is a 1440px desktop design and 105px would starve a phone.
        pastListLayout && 'md:gap-[105px]',
      )}
      elevation={ONE}
    >
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <Avatar
          firstName={coach.firstName}
          lastName={coach.lastName}
          src={coach.avatarUrl}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {isPast && !pastReadOnly ? (
            <Link
              aria-label={`View ${actionContext}`}
              className="flex min-w-0 flex-col rounded-sm text-inherit no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500"
              to={`/m/sessions/${session.id}`}
            >
              <Typography
                className="break-words md:truncate"
                variant="subtitle1"
              >
                {cardHeadline}
              </Typography>
              <Typography
                className="font-medium text-cement-400"
                variant="caption"
              >
                {cardSubhead}
              </Typography>
            </Link>
          ) : (
            <div aria-label="Session details" className="min-w-0" role="region">
              <Typography
                className="break-words md:truncate"
                variant="subtitle1"
              >
                {cardHeadline}
              </Typography>
              <Typography
                className="font-medium text-cement-400"
                variant="caption"
              >
                {cardSubhead}
              </Typography>
            </div>
          )}
          {showReflectionLink && (
            <SessionReflectionChip
              artifact={reflectionArtifact}
              className="mt-3"
            />
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 md:gap-6">
        {showJoinNow && (
          <Button
            startIcon={<Icon>videocam</Icon>}
            variant="contained"
            onClick={handleJoin}
          >
            Join Session
          </Button>
        )}
        {isDone && !pastListLayout && (
          <Chip color="secondary" label="Attended" />
        )}
        {isPast ? (
          !pastReadOnly && (
            <>
              <Feedback handleOnFeedback={handleFeedback} session={session} />
              <IconButton
                aria-label={`Session actions for ${actionContext}`}
                onClick={handleMenuClick}
              >
                <Icon>more_horiz</Icon>
              </IconButton>
              <ClickAwayListener onClickAway={handleOnClose}>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  open={open}
                  PaperProps={{
                    className: 'min-w-[137px] whitespace-nowrap rounded-xl',
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  onClose={handleOnClose}
                >
                  <MenuItem
                    aria-label={`Provide feedback for ${actionContext}`}
                    onClick={handleFeedback}
                  >
                    <ListItemIcon>
                      <Icon>add</Icon>
                    </ListItemIcon>
                    <ListItemText>Provide Feedback</ListItemText>
                  </MenuItem>
                  <MenuItem
                    aria-label={`View profile for ${coach.fullName}`}
                    onClick={handleViewProfile}
                  >
                    <ListItemIcon>
                      <Icon>account_circle</Icon>
                    </ListItemIcon>
                    <ListItemText>View Profile</ListItemText>
                  </MenuItem>
                  <MenuItem
                    aria-label={`Message ${coach.fullName} about ${session.title}`}
                    onClick={handleMessage}
                  >
                    <ListItemIcon>
                      <Icon>chat_bubble</Icon>
                    </ListItemIcon>
                    <ListItemText>Message {coach.firstName}</ListItemText>
                  </MenuItem>
                </Menu>
              </ClickAwayListener>
            </>
          )
        ) : (
          <MemberSessionActionButtons
            actionContext={actionContext}
            allowLateReschedule={
              Boolean(session.sourceAppointmentId) || isMomentsSession(session)
            }
            rescheduleAllowed={
              session.sourceAppointmentRescheduleAllowed !== false
            }
            startedAt={session.startedAt}
            onCancel={showCancel ? () => onCancel?.(session) : undefined}
            onReschedule={
              onReschedule ? () => onReschedule(session) : undefined
            }
          />
        )}
      </div>
    </Paper>
  );
};

export default MemberSessionCard;
